import { useLayoutEffect, useState } from 'react'

// The widget's box around a template card's artwork (PrepacksPresentation.tsx), and the card it sits in, which
// carries the picture itself when the template runs it under the footer (#1038).
const FRAME = '.prepack-widget-card__frame-wrap'
const CARD = '.prepack-widget-card'
const FULL_PICTURE = 'data-full-picture'

/** A card's width over its height until one is measured: the 380px design width over a typical card. */
export const FALLBACK_CARD_ASPECT = 380 / 420

/** A header picture's box until one is measured: the 380px design width over the 76px a row header or a band is tall. */
export const FALLBACK_HEADER_ASPECT = 380 / 76

// A whole-card picture's box: the artwork, or the whole card, footer included, when the picture runs under it.
const cardShapeOf = (frame) => {
  const card = frame.closest(CARD)
  return (card?.getAttribute(FULL_PICTURE) === 'true' ? card : frame).getBoundingClientRect()
}

// The box a header picture fills inside the card's artwork (bannerFills.js): the band the card draws above its
// header (.card::before) when the title sits below the picture or there is none, else the header itself, which a
// hero layout grows with its title (#978). The artwork is a same-origin srcdoc frame; until it has loaded there is
// nothing to measure.
function headerShapeOf(frame) {
  const doc = frame.querySelector('iframe')?.contentDocument
  const card = doc?.querySelector('.card')
  if (!card) return null
  const band = doc.defaultView?.getComputedStyle(card, '::before')
  if (band && band.content !== 'none' && parseFloat(band.height) > 0) return { width: card.clientWidth, height: parseFloat(band.height) }
  return doc.querySelector('.head')?.getBoundingClientRect() ?? null
}

/**
 * The shape (width / height) of `shapeOf(frame)` for the first card artwork drawn inside `ref`, as it is drawn now.
 * The artwork's height follows its legs and copy, so a ResizeObserver follows it, and a frame that reloads its card
 * is measured again; a card that is not shown (zero size, such as the other mode's card on a phone) is skipped for
 * the next one, and cards that come and go (Card / In a feed) are picked up as they mount, as is a card that starts
 * or stops carrying its picture under the footer (#1038), whose shape changes without its artwork's. Before a measurement, or
 * without ResizeObserver, it is `fallback`. The artwork scales with its box, so the shape is the same at any width.
 */
function useFrameAspect(ref, shapeOf, fallback) {
  const [aspect, setAspect] = useState(fallback)
  useLayoutEffect(() => {
    const root = ref.current
    if (!root || typeof ResizeObserver === 'undefined') return undefined
    const measure = () => {
      for (const frame of root.querySelectorAll(FRAME)) {
        const shown = frame.getBoundingClientRect()
        if (!(shown.width > 0 && shown.height > 0)) continue
        const { width, height } = shapeOf(frame) || {}
        if (!(width > 0 && height > 0)) continue
        // Rounded so a sub-pixel change of the card does not re-render the framing box.
        const next = Math.round((width / height) * 1000) / 1000
        setAspect((current) => (current === next ? current : next))
        return
      }
    }
    const resize = new ResizeObserver(measure)
    let observed = []
    const watch = () => {
      const frames = Array.from(root.querySelectorAll(FRAME))
      if (frames.length === observed.length && frames.every((frame, index) => frame === observed[index])) return
      for (const frame of observed) {
        resize.unobserve(frame)
        const card = frame.closest(CARD)
        if (card) resize.unobserve(card)
        frame.querySelector('iframe')?.removeEventListener('load', measure)
      }
      for (const frame of frames) {
        resize.observe(frame)
        // The card around it grows with its footer and "See all" row, the box a picture under the footer fills.
        const card = frame.closest(CARD)
        if (card) resize.observe(card)
        frame.querySelector('iframe')?.addEventListener('load', measure)
      }
      observed = frames
      measure()
    }
    watch()
    const mounts = typeof MutationObserver === 'undefined' ? null : new MutationObserver((records) => {
      if (records.some((record) => record.type === 'attributes')) measure()
      watch()
    })
    mounts?.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: [FULL_PICTURE] })
    return () => {
      resize.disconnect()
      mounts?.disconnect()
      for (const frame of observed) frame.querySelector('iframe')?.removeEventListener('load', measure)
    }
  }, [ref, shapeOf])
  return aspect
}

/**
 * The shape (width / height) of the first card artwork drawn inside `ref`, or of its whole card, footer included,
 * when the picture runs under the footer (#1038). A picture that fills the whole card is framed in a box of this
 * shape, so the box crops what the card crops. FALLBACK_CARD_ASPECT until measured.
 */
export function useCardAspect(ref) {
  return useFrameAspect(ref, cardShapeOf, FALLBACK_CARD_ASPECT)
}

/**
 * The shape (width / height) of the box a header picture fills on the first card artwork drawn inside `ref`: 5:1
 * for a row header or a band, taller for a hero header with the title over the picture, which grows with the
 * title. A header picture is framed in a box of this shape. FALLBACK_HEADER_ASPECT until measured.
 */
export function useHeaderAspect(ref) {
  return useFrameAspect(ref, headerShapeOf, FALLBACK_HEADER_ASPECT)
}
